import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Code, Search, Lock, ArrowRight, CheckCircle, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';

// Logos em frontend/public/images/ (logo-jlm.png, logo-barber.png, logo-clarilever.png)
const publicUrl = process.env.PUBLIC_URL || '';
const clientLogos = [
  { src: `${publicUrl}/images/logo-jlm.png`, alt: 'JLM Esquadrias de Alumínio' },
  { src: `${publicUrl}/images/logo-barber.png`, alt: 'Barber Book' },
  { src: `${publicUrl}/images/logo-clarilever.png`, alt: 'Clarilever - Especialista em venezianas industriais' },
];

const Home = () => {
  const { t } = useLanguage();
  const [scrollY, setScrollY] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const [carouselApi, setCarouselApi] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    // Intersection Observer para reveal animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-reveal]').forEach((el) => {
      observer.observe(el);
    });

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  // Autoplay do carrossel de logos
  useEffect(() => {
    if (!carouselApi) return;
    const interval = setInterval(() => {
      carouselApi.scrollNext();
    }, 2000);
    return () => clearInterval(interval);
  }, [carouselApi]);

  // Parallax calculations
  const heroParallax = scrollY * 0.5;
  const midLayerParallax = scrollY * 0.3;

  // Extrair em variáveis para evitar recursão infinita do babel-metadata-plugin
  const testimonialsList = t.common.testimonials;
  const testimonial0 = testimonialsList[0];
  const testimonial1 = testimonialsList[1];
  const testimonial2 = testimonialsList[2];
  const pentestService = t.services.pentest;
  const developmentService = t.services.development;
  const auditService = t.services.audit;
  const consultingService = t.services.consulting;

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with Multi-Layer Parallax */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Slowest background layer */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            transform: `translateY(${heroParallax}px)`,
            willChange: 'transform'
          }}
        >
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=1920)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(2px)'
            }}
          />
        </div>

        {/* Mid-layer - Circuit pattern */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            transform: `translateY(${midLayerParallax}px)`,
            willChange: 'transform'
          }}
        >
          <div className="absolute inset-0 bg-[linear-gradient(rgba(11,112,42,0.15)_1px,transparent_1px),linear-gradient(90deg,rgba(11,112,42,0.15)_1px,transparent_1px)] bg-[size:50px_50px]" />
        </div>

        {/* Gradient overlay - static */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0C9F39]/60 to-black" />

        {/* Content - Static and readable */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="space-y-8">
            {/* <div 
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#014421]/80 backdrop-blur-sm rounded-full border border-[#0B702A]/30 animate-fade-in"
              style={{ animationDelay: '0.1s' }}
            >
              <Shield className="w-4 h-4 text-[#0B702A]" />
              <span className="text-sm text-gray-300">Cibersegurança Premium</span>
            </div> */}

            <h1
              className="text-5xl md:text-7xl font-bold text-white leading-tight animate-fade-in"
              style={{ animationDelay: '0.2s' }}
            >
              {t.hero.title}
            </h1>

            <p
              className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed animate-fade-in"
              style={{ animationDelay: '0.3s' }}
            >
              {t.hero.subtitle}
            </p>

            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8 animate-fade-in"
              style={{ animationDelay: '0.4s' }}
            >
              <Link
                to="/contact"
                className="group relative px-8 py-4 bg-[#0B702A] text-white font-semibold rounded-lg transition-all duration-300 flex items-center gap-2 shadow-lg overflow-hidden cta-glow"
              >
                <span className="relative z-10">{t.hero.primaryCTA}</span>
                <ArrowRight className="relative z-10 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0C9F39] to-[#0B702A] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 bg-transparent border-2 border-[#0B702A] text-white font-semibold rounded-lg hover:bg-[#0B702A]/10 transition-all duration-300 flex items-center gap-2 hover:scale-105"
              >
                {t.hero.secondaryCTA}
              </Link>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            <div className="text-center space-y-2 animate-fade-in" style={{ animationDelay: '0.5s' }}>
              <div className="text-3xl md:text-4xl font-bold text-[#0B702A]">500+</div>
              <div className="text-sm text-gray-400">{t.common.heroStats.secureProjects}</div>
            </div>
            <div className="text-center space-y-2 animate-fade-in" style={{ animationDelay: '0.6s' }}>
              <div className="text-3xl md:text-4xl font-bold text-[#0B702A]">98%</div>
              <div className="text-sm text-gray-400">{t.common.heroStats.satisfaction}</div>
            </div>
            <div className="text-center space-y-2 animate-fade-in" style={{ animationDelay: '0.7s' }}>
              <div className="text-3xl md:text-4xl font-bold text-[#0B702A]">24/7</div>
              <div className="text-sm text-gray-400">{t.common.heroStats.support}</div>
            </div>
            <div className="text-center space-y-2 animate-fade-in" style={{ animationDelay: '0.8s' }}>
              <div className="text-3xl md:text-4xl font-bold text-[#0B702A]">15+</div>
              <div className="text-sm text-gray-400">{t.common.heroStats.yearsExperience}</div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator with pulse */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#0B702A] rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-[#0B702A] rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Client Logos - Carrossel */}
      <section className="relative py-16 bg-[#014421]/30 border-y border-[#0B702A]/20 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            transform: `translateY(${scrollY * 0.1}px)`,
            willChange: 'transform'
          }}
        >
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-[#0B702A]/20 via-transparent to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-400 mb-8 text-sm uppercase tracking-wider">
            {t.common.companies}
          </p>
          <Carousel
            setApi={setCarouselApi}
            opts={{ loop: true, align: 'center' }}
            className="w-full"
          >
            <CarouselContent className="-ml-2 md:-ml-4">
              {[...clientLogos, ...clientLogos].map((logo, index) => (
                <CarouselItem
                  key={`${logo.alt}-${index}`}
                  className="pl-2 md:pl-4 basis-1/2 sm:basis-1/3 md:basis-1/4 lg:basis-1/5"
                >
                  <div className="flex justify-center items-center py-4 px-2">
                    <div className="grayscale hover:grayscale-0 transition-all duration-300 opacity-60 hover:opacity-100 hover:scale-110 h-20 flex items-center">
                      <img
                        src={logo.src}
                        alt={logo.alt}
                        className="h-16 w-auto max-h-20 object-contain object-center"
                      />
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-0 -translate-y-1/2 border-[#0B702A]/30 bg-black/80 hover:bg-[#0B702A]/20 text-white hover:text-white" />
            <CarouselNext className="right-0 -translate-y-1/2 border-[#0B702A]/30 bg-black/80 hover:bg-[#0B702A]/20 text-white hover:text-white" />
          </Carousel>
        </div>
      </section>

      {/* Services Section with Floating Parallax */}
      <section className="py-24 bg-black relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="text-center mb-16 space-y-4"
            id="services-title"
            data-reveal
          >
            <h2 className={`text-4xl md:text-5xl font-bold text-white transition-all duration-1000 ${isVisible['services-title'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {t.services.title}
            </h2>
            <p className={`text-xl text-gray-400 max-w-3xl mx-auto transition-all duration-1000 delay-100 ${isVisible['services-title'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {t.services.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Service Card 1 */}
            <div
              id="service-1"
              data-reveal
              className={`group p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl transition-all duration-700 hover:bg-[#014421]/50 hover:border-[#0B702A]/60 hover:scale-105 hover:shadow-lg hover:shadow-[#0B702A]/20 ${isVisible['service-1'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
              style={{
                transitionDelay: '0s',
                transform: isVisible['service-1'] ? `translateY(${Math.sin(scrollY * 0.002) * 10}px)` : 'translateY(20px)'
              }}
            >
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#0B702A]/30 transition-colors group-hover:scale-110">
                <Shield className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{pentestService.title}</h3>
              <p className="text-gray-400 mb-6 leading-relaxed">{pentestService.description}</p>
              <ul className="space-y-2">
                {pentestService.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                    <CheckCircle className="w-4 h-4 text-[#0B702A] flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Service Card 2 */}
            <div
              id="service-2"
              data-reveal
              className={`group p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl transition-all duration-700 hover:bg-[#014421]/50 hover:border-[#0B702A]/60 hover:scale-105 hover:shadow-lg hover:shadow-[#0B702A]/20 ${isVisible['service-2'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
              style={{
                transitionDelay: '0.1s',
                transform: isVisible['service-2'] ? `translateY(${Math.sin(scrollY * 0.002 + 1) * 10}px)` : 'translateY(20px)'
              }}
            >
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#0B702A]/30 transition-colors group-hover:scale-110">
                <Code className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{developmentService.title}</h3>
              <p className="text-gray-400 mb-6 leading-relaxed">{developmentService.description}</p>
              <ul className="space-y-2">
                {developmentService.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                    <CheckCircle className="w-4 h-4 text-[#0B702A] flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Service Card 3 */}
            <div
              id="service-3"
              data-reveal
              className={`group p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl transition-all duration-700 hover:bg-[#014421]/50 hover:border-[#0B702A]/60 hover:scale-105 hover:shadow-lg hover:shadow-[#0B702A]/20 ${isVisible['service-3'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
              style={{
                transitionDelay: '0.2s',
                transform: isVisible['service-3'] ? `translateY(${Math.sin(scrollY * 0.002 + 2) * 10}px)` : 'translateY(20px)'
              }}
            >
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#0B702A]/30 transition-colors group-hover:scale-110">
                <Search className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{auditService.title}</h3>
              <p className="text-gray-400 mb-6 leading-relaxed">{auditService.description}</p>
              <ul className="space-y-2">
                {auditService.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                    <CheckCircle className="w-4 h-4 text-[#0B702A] flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Service Card 4 */}
            <div
              id="service-4"
              data-reveal
              className={`group p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl transition-all duration-700 hover:bg-[#014421]/50 hover:border-[#0B702A]/60 hover:scale-105 hover:shadow-lg hover:shadow-[#0B702A]/20 ${isVisible['service-4'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
              style={{
                transitionDelay: '0.3s',
                transform: isVisible['service-4'] ? `translateY(${Math.sin(scrollY * 0.002 + 3) * 10}px)` : 'translateY(20px)'
              }}
            >
              <div className="w-14 h-14 bg-[#0B702A]/20 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#0B702A]/30 transition-colors group-hover:scale-110">
                <Lock className="w-7 h-7 text-[#0B702A]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">{consultingService.title}</h3>
              <p className="text-gray-400 mb-6 leading-relaxed">{consultingService.description}</p>
              <ul className="space-y-2">
                {consultingService.features.map((feature, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-gray-400">
                    <CheckCircle className="w-4 h-4 text-[#0B702A] flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Brand Manifesto with Cinematic Parallax & Zoom */}
      <section
        className="relative py-32 overflow-hidden"
        id="manifesto"
        data-reveal
      >
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to right, white, #0C9F39, white)',
            transform: `scale(${1 + scrollY * 0.0001})`,
            willChange: 'transform'
          }}
        />
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `scale(${1 + scrollY * 0.00015})`,
            willChange: 'transform'
          }}
        />
        <div className={`relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${isVisible['manifesto'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
          <Shield className="w-20 h-20 text-black mx-auto mb-8 animate-pulse" />
          <h2 className="text-4xl md:text-6xl font-bold text-black mb-6">
            {t.common.manifesto}
          </h2>
          <p className="text-xl md:text-2xl text-gray-800 leading-relaxed">
            {t.common.manifestoText}
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="text-center mb-16"
            id="testimonials-title"
            data-reveal
          >
            <h2 className={`text-4xl md:text-5xl font-bold text-white mb-4 transition-all duration-1000 ${isVisible['testimonials-title'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
              {t.common.clients}
            </h2>
            <p className={`text-xl text-gray-400 transition-all duration-1000 delay-100 ${isVisible['testimonials-title'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}>
              {t.common.clientsSubtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div
              id="testimonial-1"
              data-reveal
              className={`p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:border-[#0B702A]/60 transition-all duration-700 hover:scale-105 ${isVisible['testimonial-1'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
            >
              <div className="flex gap-1 mb-4">
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">"{testimonial0.quote}"</p>
              <div className="flex items-center gap-4">
                <img src={`${publicUrl}/images/logo-jlm.png`} alt={testimonial0.company || testimonial0.name} className="w-12 h-12 rounded-lg object-contain bg-white/5 p-0.5" />
                <div>
                  <div className="font-semibold text-white">{testimonial0.name}</div>
                  <div className="text-sm text-gray-400">{testimonial0.role}{testimonial0.company ? ` · ${testimonial0.company}` : ''}</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div
              id="testimonial-2"
              data-reveal
              className={`p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:border-[#0B702A]/60 transition-all duration-700 hover:scale-105 ${isVisible['testimonial-2'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
              style={{ transitionDelay: '0.1s' }}
            >
              <div className="flex gap-1 mb-4">
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">"{testimonial1.quote}"</p>
              <div className="flex items-center gap-4">
                <img src={`${publicUrl}/images/logo-clarilever.png`} alt={testimonial1.company || testimonial1.name} className="w-12 h-12 rounded-lg object-contain bg-white/5 p-0.5" />
                <div>
                  <div className="font-semibold text-white">{testimonial1.name}</div>
                  <div className="text-sm text-gray-400">{testimonial1.role}{testimonial1.company ? ` · ${testimonial1.company}` : ''}</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div
              id="testimonial-3"
              data-reveal
              className={`p-8 bg-[#014421]/30 border border-[#0B702A]/20 rounded-xl hover:border-[#0B702A]/60 transition-all duration-700 hover:scale-105 ${isVisible['testimonial-3'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'}`}
              style={{ transitionDelay: '0.2s' }}
            >
              <div className="flex gap-1 mb-4">
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
                <Star className="w-5 h-5 text-[#0B702A] fill-[#0B702A]" />
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed">"{testimonial2.quote}"</p>
              <div className="flex items-center gap-4">
                <img src={`${publicUrl}/images/logo-barber.png`} alt={testimonial2.company || testimonial2.name} className="w-12 h-12 rounded-lg object-contain bg-white/5 p-0.5" />
                <div>
                  <div className="font-semibold text-white">{testimonial2.name}</div>
                  <div className="text-sm text-gray-400">{testimonial2.role}{testimonial2.company ? ` · ${testimonial2.company}` : ''}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA with High-Impact Parallax */}
      <section
        className="relative py-32 overflow-hidden"
        id="final-cta"
        data-reveal
      >
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, black, rgba(12, 159, 57, 0.8), black)',
            transform: `translateY(${scrollY * 0.15}px)`,
            willChange: 'transform'
          }}
        />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1563206767-5b18f218e8de?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: `scale(${1 + scrollY * 0.00008})`,
            willChange: 'transform'
          }}
        />
        <div className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${isVisible['final-cta'] ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {t.common.finalCTA}
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            {t.common.finalCTASubtitle}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-10 py-5 bg-[#0B702A] text-white text-lg font-semibold rounded-lg transition-all duration-300 shadow-lg hover:shadow-[#0B702A]/50 hover:scale-110 cta-pulse relative overflow-hidden group"
          >
            <span className="relative z-10">{t.common.finalCTAButton}</span>
            <ArrowRight className="relative z-10 w-6 h-6 group-hover:translate-x-2 transition-transform" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0C9F39] to-[#0B702A] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </Link>
        </div>
      </section>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
          opacity: 0;
        }

        .cta-glow::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: linear-gradient(45deg, #0B702A, #0C9F39, #0B702A);
          border-radius: 0.5rem;
          opacity: 0;
          filter: blur(10px);
          transition: opacity 0.3s;
          z-index: -1;
        }

        .cta-glow:hover::before {
          opacity: 0.7;
          animation: glow-pulse 2s ease-in-out infinite;
        }

        @keyframes glow-pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.9; }
        }

        .cta-pulse {
          animation: subtle-pulse 3s ease-in-out infinite;
        }

        @keyframes subtle-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.02); }
        }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Home;
